import { businessToday, CLIENT_STAGE_LABELS, type ClientStage } from '@mexp/shared';

export const STAGE_ORDER: ClientStage[] = ['received', 'in_preparation', 'final_review', 'completed'];

export const STAGE_COLOR: Record<ClientStage, string> = {
  received: 'gray',
  in_preparation: 'brand',
  final_review: 'violet',
  completed: 'teal',
  cancelled: 'red',
};

/** One line on what happens at each stage, in the client's terms. */
export const STAGE_HELP: Record<Exclude<ClientStage, 'cancelled'>, string> = {
  received: "Makin's regulatory team has your requisition and is planning the work.",
  in_preparation: 'Each document is being prepared and checked by the team that owns it.',
  final_review: 'The compiled documents are with Makin for a last approval before release.',
  completed: 'Approved documents are ready for you to download.',
};

export function stageLabel(stage: ClientStage): string {
  return CLIENT_STAGE_LABELS[stage];
}

const DAY = 86_400_000;

function toUtc(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

/** Whole days from today (business calendar) to a `YYYY-MM-DD` date; negative when past. */
export function daysUntil(date: string, today = businessToday()): number {
  return Math.round((toUtc(date) - toUtc(today)) / DAY);
}

export function addDays(date: string, days: number): string {
  return new Date(toUtc(date) + days * DAY).toISOString().slice(0, 10);
}

const DAY_MONTH = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});
const FULL = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

export function dayMonth(date: string): string {
  return DAY_MONTH.format(new Date(toUtc(date)));
}

export function fullDate(date: string): string {
  return FULL.format(new Date(toUtc(date)));
}

/**
 * The date Makin gives the client for a dossier (RA sets it once the work is planned). It is shown as a plain
 * expectation: clients never see "overdue".
 */
export function expectedLabel(requiredBy: string | null, format: 'short' | 'full' = 'short'): string {
  if (!requiredBy) return 'Date to be confirmed';
  return `Target completion ${format === 'full' ? fullDate(requiredBy) : dayMonth(requiredBy)}`;
}
